import React, { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Grid, } from "@mui/material";
import { useTranslation } from "react-i18next";
import DataTable from "../common/DataTable";
import { GET_IMPORT_BUDGET_PLANNING_REPORT } from "../../services/apiRoutes";
import { AuthContext } from "../../context/AuthContext";
import { getSuppliersList } from "../../services/CommonServices";
import CustomLoader from "../common/CustomLoader";
import CustomReport from "../common/CustomReport";
import { genericPost } from "../../services/authServices";
import CustomTextField from "../common/CustomTextField";
import CustomFilter from "../common/CustomFilter";
import CustomButton from "../common/CustomButton";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomAutocomplete from "../common/CustomAutoComplete";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";


const title = "Import Budget Planning Report";

const ImportBudgetPlanningReport = () => {
    const companyDateFormat = useCompanyDateFormat();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [permission, setPermissions] = useState()
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    // ✅ Pagination states
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);

    const [filterPayload, setFilterPayload] = useState({
        shipmentId: '',
        supplierNamesId: '',
        fromDate: '',
        toDate: '',
    });

    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])

    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: '',
                supplierNamesId: '',
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
    }

    const columns = useMemo(
        () => [
            {
                accessorKey: "index",
                header: "SN.",
                size: 50,
                tableHeader: { justifyContent: "center" },
                tableStyle: { textAlign: "center" }
            },

            {
                accessorKey: "shipmentId",
                header: "Shipment ID",
                size: 120,
                tableHeader: { justifyContent: "center" },
                tableStyle: { textAlign: "center" }
            },

            {
                accessorKey: "proformaInvoiceNo",
                header: "Proforma Invoice",
                size: 150,
                tableHeader: { justifyContent: "center" },
                tableStyle: { textAlign: "center" }
            },

            {
                accessorKey: "commercialInvoiceNo",
                header: "Commercial Invoice",
                size: 160,
                tableHeader: { justifyContent: "center" },
                tableStyle: { textAlign: "center" }
            },


            {
                accessorKey: "seaFreight",
                header: "Sea Freight",
                size: 120,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },

            {
                accessorKey: "clearingAgencyCharge",
                header: "Clearing Agency Charge",
                size: 180,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },

            {
                accessorKey: "freightForwardAgencyCharge",
                header: "Freight Forward Charge",
                size: 190,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },

            {
                accessorKey: "cfsLocalCharges",
                header: "CFS Local Charges",
                size: 150,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },

            {
                accessorKey: "shippingLineCharges",
                header: "Shipping Line Charges",
                size: 170,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },

            {
                accessorKey: "blCharges",
                header: "BL Charges",
                size: 120,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },

            {
                accessorKey: "othersCharges",
                header: "Other Charges",
                size: 140,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },

            {
                accessorKey: "dutyCharge",
                header: "Duty",
                size: 120,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right", fontWeight: 500 }
            },

            {
                accessorKey: "taxCharge",
                header: "Tax",
                size: 120,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right", fontWeight: 500 }
            },

            {
                accessorKey: "transportCharge",
                header: "Transport Charge",
                size: 160,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },
            {
                accessorKey: "conversionRate",
                header: "conversion Rate",
                size: 160,
                tableHeader: { justifyContent: "flex-end" },
                tableStyle: { textAlign: "right" }
            },
        ],
        [permission]
    );
    const fetchData = async (pageIndex = 0, pageSize = 10,) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };

            const response = await genericPost(GET_IMPORT_BUDGET_PLANNING_REPORT, payload, {});
            if (response.data.success && response.data.planningBudget) {

                const shipmentData = response.data.planningBudget.map((item, index) => {

                    const baseCurrency = item?.baseCurrency?.currency_symbol || '';
                    const currencySymbol = item?.currency?.currency_symbol || '';

                    const formatAmount = (value) =>
                        `${baseCurrency} ${parseFloat(value || 0).toFixed(2)}`;
                    const formatAmountCompany = (value) =>
                        `${currencySymbol} ${parseFloat(value || 0).toFixed(2)}`;

                    return {
                        index: index + 1,

                        shipmentId: item.shipmentId || '',

                        proformaInvoiceNo: item?.ProformaInvoice?.proformaInvoiceNo || '',

                        commercialInvoiceNo: item?.CommercialInvoice?.commercialInvoiceNo || '',

                        seaFreight: formatAmountCompany(item.seaFreight),

                        clearingAgencyCharge: formatAmount(item.clearingAgencyCharge),

                        freightForwardAgencyCharge: formatAmount(item.freightForwardAgencyCharge),

                        cfsLocalCharges: formatAmount(item.cfsLocalCharges),

                        shippingLineCharges: formatAmount(item.shippingLineCharges),

                        blCharges: formatAmount(item.blCharges),

                        othersCharges: formatAmount(item.othersCharges),

                        dutyCharge: formatAmount(item.dutyCharge),

                        taxCharge: formatAmount(item.taxCharge),

                        transportCharge: formatAmount(item.transportCharge),
                        conversionRate: formatAmount(item.conversionRate),
                    };
                });
                setData(shipmentData);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setError('No import shipments found');
            }
        } catch (error) {
            setError('Error fetching import shipments');
            console.error('Error fetching import shipments', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, token])

    const defaultView = 'importbudgetplanningreport';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("supplierNamesId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.supplierNamesId) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );
    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };
    return (
        <>
            <>
                <CustomReport
                    defaultView={defaultView}
                    mainContent={getMainContent(mainContent, permission)}
                />
                {!permission?.view && <AccessDenied />}
            </>
        </>
    );
};
export default ImportBudgetPlanningReport;
